import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { ApplicationState } from '../../../store';
import PageWrapper from '.';
import { getStudentRecord, registerStudentView } from '../../../store/student/actions';
import { getApplicantRecord, getRouteStatus } from '@/store/apply/actions';
import { ErrorBoundary } from '../../../ErrorBoundary';
import { UserRole } from '../../../store/auth/types';
import { getDisplayName } from '../../../utilities/formatting';
import { useLocation, useParams } from 'react-router-dom';
import { getApplyConfigRecord } from '@/store/apply_config/actions';
import { isPartnerRole } from '@/utilities/roles/permissions';
import { useStudentViewRegistration } from '../../../hooks/useStudentViewRegistration';

interface DispatchProps {
  getStudent: (id: string, university: string, intakeId: string, offerId: string) => void;
  getApplicant: (userId: string, applicationId: string, uni?: string) => void;
  getApplyUniConfig: (uni: string) => void;
  getApplicantRouteStatus: (uni: string, applicantId: string, applicationId?: string) => void;
  onRegisterStudentView: () => void;
}

interface StateProps {
  isLoading: boolean;
  isStudentLoaded: boolean;
  studentNumber: string;
  userRole: UserRole;
  groups: Array<string>;
  displayName?: string;
  loadedUserId?: string;
}

type Props = StateProps & DispatchProps;

type Options = Partial<{
  contentWidth: string;
  requestStudent: boolean;
  withLayout: boolean;
}>;

function wrapPage(Component: React.ComponentClass<any> | React.FC<any>, options?: Options) {
  const PopulateContainer: React.FC<Props> = props => {
    const {
      getStudent,
      getApplicant,
      getApplyUniConfig,
      getApplicantRouteStatus,
      onRegisterStudentView,
      isLoading,
      isStudentLoaded,
      studentNumber,
      userRole,
      groups,
      displayName,
      loadedUserId,
    } = props;
    const location = useLocation();
    const { pathname } = location;
    const { uni, userId, applicationId, id: studentId, intakeId, offerId } = useParams();

    const { contentWidth, requestStudent } = useMemo<Options>(() => {
      const defaultOptions = { requestStudent: true, withLayout: false };
      return { ...defaultOptions, ...options };
    }, []);

    useEffect(() => {
      if (requestStudent && pathname.includes('application')) {
        const shouldFetchApplicant = userId && loadedUserId !== userId;
        if (shouldFetchApplicant) {
          getApplicant(userId, applicationId, uni);
          getApplicantRouteStatus(uni, userId, applicationId);

          if (!isPartnerRole(userRole)) {
            getApplyUniConfig(uni);
          }
        }
      }
    }, [loadedUserId, pathname, userId, uni, applicationId]);

    // Track registered student views and clear when pathname changes (navigating away)
    const { registerViewIfNeeded } = useStudentViewRegistration([location.pathname]);

    useEffect(() => {
      if (requestStudent && pathname.includes('student')) {
        const currentStudentNumber = String(studentNumber);
        const targetStudentId = String(studentId);

        if (uni && studentId && currentStudentNumber !== targetStudentId) {
          getStudent(studentId, uni, intakeId, offerId);
        } else if (isStudentLoaded && currentStudentNumber === targetStudentId && uni && intakeId && offerId) {
          // Create a unique key for this student view registration
          const viewKey = `${targetStudentId}#${intakeId}#${offerId}`;
          registerViewIfNeeded(viewKey, onRegisterStudentView);
        }
      }
    }, [
      pathname,
      requestStudent,
      uni,
      studentId,
      offerId,
      studentNumber,
      isStudentLoaded,
      onRegisterStudentView,
      registerViewIfNeeded,
    ]);

    return (
      <ErrorBoundary resetKey={pathname}>
        <PageWrapper
          isLoading={isLoading || !isStudentLoaded}
          student_number={studentNumber}
          contentWidth={contentWidth}
          userRole={userRole}
          groups={groups}
          displayName={displayName}
        >
          {isStudentLoaded ? (
            <>
              <div id="content-loaded" />
              <Component {...props} />
            </>
          ) : null}
        </PageWrapper>
      </ErrorBoundary>
    );
  };

  const mapStateToProps = ({ auth, config, data, student, ui, apply }: ApplicationState): StateProps => ({
    isLoading: ui.isLoading,
    isStudentLoaded: data.isStudentLoaded,
    studentNumber: student.core.student_number,
    userRole: auth.role,
    groups: auth.groups,
    displayName: getDisplayName(auth, config),
    loadedUserId: apply.application?.userId,
  });

  const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    getStudent: (id: string, university: string, intakeId: string, offerId: string) => {
      return dispatch(getStudentRecord(id, university, intakeId, offerId));
    },
    getApplicant: (userId: string, applicationId: string, uni?: string) => {
      return dispatch(getApplicantRecord(userId, applicationId, uni));
    },
    getApplyUniConfig: (uni: string) => {
      return dispatch(getApplyConfigRecord(uni));
    },
    getApplicantRouteStatus: (uni: string, applicantId: string, applicationId?: string) => {
      return dispatch(getRouteStatus(uni, applicantId, applicationId));
    },
    onRegisterStudentView: () => {
      return dispatch(registerStudentView());
    },
  });

  return connect(mapStateToProps, mapDispatchToProps)(PopulateContainer);
}

export default wrapPage;
